import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { ago, AudienceId, EmailMarketingProvider, RegionId } from "@sp-crm/core";
import { RecentAudienceSyncsQuery, useRecentAudienceSyncsQuery } from "generated/graphql";
import {
    Popover,
    PopoverButton,
    PopoverPanel,
} from "lib/headlessui/components/popover/popover";
import React from "react";

const RECENT_LIMIT = 5;

type RecentSync = RecentAudienceSyncsQuery["recentAudienceSyncs"][number];

interface AudienceSyncRecentProps {
    audienceId: AudienceId;
    regionId: RegionId;
    provider: EmailMarketingProvider;
}

export const AudienceSyncRecent: React.FC<AudienceSyncRecentProps> = ({
    audienceId,
    regionId,
    provider,
}) => {
    const recentQuery = useRecentAudienceSyncsQuery(
        { input: { audienceId, regionId, provider, limit: RECENT_LIMIT } },
        {
            refetchInterval: data => {
                const status = data?.recentAudienceSyncs?.[0]?.status;
                return status === "Queued" || status === "Running" ? 2000 : false;
            },
        },
    );
    const recent = recentQuery.data?.recentAudienceSyncs ?? [];

    return (
        <Popover className="relative inline-block">
            {({ open }) => (
                <>
                    <PopoverButton
                        className="text-sm text-brand-600 hover:underline focus:outline-none"
                        onClick={() => recentQuery.refetch()}>
                        <span className="inline-flex items-center">
                            View recent attempts
                            <ChevronDownIcon
                                className={`w-4 h-4 ml-1 transition duration-50 ${
                                    open ? "rotate-180 transform" : ""
                                }`}
                            />
                        </span>
                    </PopoverButton>
                    <PopoverPanel className="absolute z-10 mt-1">
                        <div className="shadow-lg w-96 bg-white rounded border border-gray-200 max-h-72 overflow-y-auto">
                            {recent.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500">
                                    No recent sync attempts.
                                </div>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {recent.map(item => (
                                        <RecentAttemptRow
                                            key={item.historyId}
                                            attempt={item}
                                        />
                                    ))}
                                </ul>
                            )}
                        </div>
                    </PopoverPanel>
                </>
            )}
        </Popover>
    );
};

const RecentAttemptRow: React.FC<{ attempt: RecentSync }> = ({ attempt }) => {
    const when = attempt.completedAt ?? attempt.startedAt ?? attempt.queuedAt;
    return (
        <li className="px-3 py-2 text-sm">
            <div className="flex items-center justify-between space-x-2">
                <StatusBadge status={attempt.status} />
                <span className="text-gray-500 text-xs">{ago(when)}</span>
            </div>
            <div className="mt-1 text-xs text-gray-600">
                {attempt.status === "Success" || attempt.status === "PartialSuccess" ? (
                    <span>
                        {attempt.membersAdded} added, {attempt.membersUpdated} updated,{" "}
                        {attempt.membersRemoved} removed
                        {typeof attempt.durationMs === "number"
                            ? ` · ${formatDuration(attempt.durationMs)}`
                            : ""}
                    </span>
                ) : attempt.status === "Failed" ? (
                    <span className="text-red-600">
                        {attempt.errorMessage ?? "Unknown error"}
                    </span>
                ) : (
                    <span>In progress</span>
                )}
            </div>
        </li>
    );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const cls = badgeClass(status);
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
            {status}
        </span>
    );
};

function badgeClass(status: string): string {
    switch (status) {
        case "Success":
            return "bg-green-100 text-green-800";
        case "PartialSuccess":
            return "bg-yellow-100 text-yellow-800";
        case "Failed":
            return "bg-red-100 text-red-800";
        case "Running":
            return "bg-blue-100 text-blue-800";
        case "Queued":
            return "bg-gray-100 text-gray-700";
        default:
            return "bg-gray-100 text-gray-700";
    }
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.round(ms / 100) / 10;
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const rem = Math.round(seconds - minutes * 60);
    return `${minutes}m ${rem}s`;
}
